<template>
  <div id="dynamic_crm_card" class="h-full self-start">
    <div
      v-if="isConfigured && !isEditing"
      id="entity_card_section"
      class="h-full"
    >
      <component
        :is="entityCardComponent"
        v-if="entityCardComponent"
        :entity-type="entityType"
        :view-id="section.config.view_id"
        :card-name="section.config.card_name"
        :user-id="computedUserId"

        :service="props.service"
      />
    </div>

    <div
      v-else
      id="configuration_section"
      class="flex flex-col justify-between h-full gap-6"
    >
      <div class="flex flex-col gap-6">
        <div id="card_name" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color"> Card Name </span>
          <a-input
            v-model:value="cardName"
            class="input_medium"
            placeholder="Enter a card name"
          />
        </div>

        <div id="view_selection" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color"> Select a view </span>
          <a-select
            v-model:value="selectedViewID"
            class="w-full"
            placeholder="Select a view"
            :options="viewsList"
            :field-names="{ label: 'name', value: 'id' }"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>
          </a-select>
        </div>
      </div>

      <a-button
        class="primary_medium w-fit self-end"
        :loading="saving"
        :disabled="!selectedViewID || !cardName"
        @click="handleSaveChanges"
        >{{ saving ? "Saving.." : "Save changes" }}</a-button
      >
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import CRMService from "@/src/api/CRMService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import {
  componentKeyToEntityTypeMapping,
  entityEndpointMap,
} from "@/src/constants/dashboardMappings.js";
import { SERVICE_REQUIRED_ENTITY_TYPES } from "@/src/constants/crmConstants.js";
import DynamicCRMSection from "@/src/components/dashboard_components/sections/DynamicCRMSection.vue";

const props = defineProps({
  source: { type: String, required: true },
  section: { type: Object, required: true },
  rowIndex: { type: Number, required: true },
  sectionIndex: { type: Number, required: true },
  globalUserId: { type: Number, default: null },

  service: { type: String, default: null },
});

const store = useStore();

const saving = ref(false);
const cardName = ref(props.section.config?.card_name || "");
const selectedViewID = ref(props.section.config?.view_id || null);
const viewsList = ref([]);

const components = computed(() => store.getters["dashboard/components"]);

const dashboardDetails = computed(
  () => store.getters["dashboard/dashboardDetails"],
);

const componentKey = computed(() => {
  const component = components.value?.find(
    (c) => c.id === props.section.component_id,
  );
  return component?.component_key || null;
});

const entityType = computed(() => {
  if (componentKey.value) {
    return componentKeyToEntityTypeMapping[componentKey.value] || null;
  }
  return null;
});

const entityEndpoint = computed(() => {
  if (!entityType.value) return null;
  const endpoint = entityEndpointMap[entityType.value];
  if (!endpoint) {
    console.error(`Unknown entity type: ${entityType.value}`);
    return null;
  }
  return endpoint;
});

const entityCardComponent = computed(() => {
  if (!entityType.value) return null;

  const mapping = {
    contact: DynamicCRMSection,
    lead: DynamicCRMSection,
    company: DynamicCRMSection,
    deal: DynamicCRMSection,
    customers: DynamicCRMSection,
    crm_tasks: DynamicCRMSection,
  };

  return mapping[entityType.value] || null;
});

const computedUserId = computed(() => {
  return props.globalUserId;
});


const isEditing = computed(() =>
  store.getters["dashboard/isSectionEditing"](props.rowIndex, props.sectionIndex),
);

const isConfigured = computed(() => {
  const hasViewId = !!props.section.config?.view_id;
  const hasCardName = !!props.section.config?.card_name;

  return hasViewId && hasCardName;
});

const handleSaveChanges = async () => {
  try {
    saving.value = true;

    if (props.rowIndex !== null && props.sectionIndex !== null) {
      await store.dispatch("dashboard/updateSectionConfig", {
        rowIndex: props.rowIndex,
        sectionIndex: props.sectionIndex,
        config: {
          card_name: cardName.value,
          view_id: selectedViewID.value,
        },
      });

      store.dispatch("dashboard/clearSectionEditing", {
        rowIndex: props.rowIndex,
        sectionIndex: props.sectionIndex,
      });

      const updatedLayout = store.getters["dashboard/dashboardLayout"];

      const payload = {
        name: dashboardDetails.value?.name,
        layout: updatedLayout,
      };

      const response = await DashboardService.builder.updateDashboard(
        dashboardDetails.value?.id,
        payload,
      );

      if (response?.success) {
        store.dispatch("dashboard/setDashboardDetails", response.data);
      }
    }
  } catch (error) {
    console.error("An error occurred while saving changes: ", error);
  } finally {
    saving.value = false;
  }
};

const fetchViews = async () => {
  if (!entityType.value) return;

  try {
    const serviceParam = SERVICE_REQUIRED_ENTITY_TYPES.includes(entityType.value)
      ? (props.service === 'customer_success' ? 'customer_success' : 'crm')
      : null;
    const response = await CRMService.fetchDynamicEntityViews(
      entityType.value,
      null,
      serviceParam,
    );

    if (response?.success) {
      viewsList.value = [
        ...(response?.activeViews || []),
        ...(response?.inActiveViews || []),
      ];
    }
  } catch (error) {
    console.error("An error occurred while fetching views: ", error);
  }
};

watch(
  () => props.section.config,
  (newConfig) => {
    cardName.value = newConfig?.card_name || "";
    selectedViewID.value = newConfig?.view_id || null;
  },
  { deep: true, immediate: true },
);

onMounted(async () => {
  await fetchViews();
});
</script>
